// Pure helpers behind the ER Charges tab.
//
// Kept out of the components because the money arithmetic is worth testing
// without a DOM. The field names below are no longer guesses: they are the
// charge-capture-service payload, read off a real emergency billing account.

/** Parse an API number. "" and null mean absent, which is not the same as 0. */
const num = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** The two event kinds the ledger carries. */
export const CHARGE = "CHARGE";
export const CANCELLATION = "CANCELLATION";

/**
 * `processing_status` on a CHARGE row.
 *
 * PENDING is a real, transient state — a charge sits there for a few seconds
 * before the billing service processes it, and longer if processing failed. It
 * will be billed, so it counts; it is labelled so nobody reads it as settled.
 * IGNORED is where a charge reversed before it processed ends up: the row is
 * patched in place and no cancellation event is ever written for it.
 */
export const PENDING = "PENDING";
export const PROCESSED = "PROCESSED";
export const IGNORED = "IGNORED";

/**
 * What one line of the add-charge dialog will cost: the catalogue fee, or the
 * typed override where there is one, times the quantity.
 *
 * An empty override box means "use the catalogue fee" — charging zero because
 * someone cleared the field would be silent data loss. An explicit 0 is honoured.
 */
export function lineGross(line) {
  const override = num(line?.amount);
  const fee = override !== null ? override : num(line?.fee);
  const quantity = num(line?.quantity);
  return (fee ?? 0) * (quantity === null ? 1 : quantity);
}

/** A service-catalogue row (`search_medical_services/`) as the picker wants it. */
export function normalizeService(row = {}) {
  return {
    id: row.id ?? row.medical_service_id ?? null,
    code: row.service_code || row.code || "",
    // `term` is what search_medical_services/ actually serializes the name as;
    // the billing doc describes a serializer sending `name`. Accept either.
    name: row.name || row.term || row.service_name || "",
    // null, not 0: a service with no preset fee needs an amount from the user;
    // one that genuinely costs nothing does not.
    fee: num(row.fee ?? row.rate ?? row.price),
    unit: row.unit || "",
    bedCategoryId: row.emergency_bed_category_id ?? null,
  };
}

/**
 * The bed service mapped to the category the patient is actually lying in, for
 * preselection. Returns null unless the mapping is explicit: guessing by name
 * would preselect the wrong bed charge, and that costs the patient money.
 *
 * Depends on `emergency_bed_category_id` reaching the client on catalogue rows.
 * The column exists; until the serializer exposes it this always returns null
 * and the dialog simply opens without a preselection.
 */
export function pickBedService(services, bedCategoryId) {
  if (bedCategoryId === null || bedCategoryId === undefined || bedCategoryId === "") {
    return null;
  }
  const target = String(bedCategoryId);
  return (
    (services || []).find(
      (s) => s?.bedCategoryId != null && String(s.bedCategoryId) === target
    ) || null
  );
}

/**
 * One event from the visit's charge ledger.
 *
 * A cancellation reports `gross_amount` as a **positive** number — it is the
 * size of the reversal, not a signed movement — so `signedGross` is what any
 * arithmetic should use. Reading `gross_amount` directly turns a ₹1,600
 * reversal into a second ₹1,600 charge.
 */
export function normalizeCharge(row = {}) {
  const kind = String(row.event_type || CHARGE).toUpperCase();
  const state = String(row.processing_status || "").toUpperCase();
  const gross = num(row.gross_amount) ?? 0;

  return {
    id: row.id ?? null,
    kind,
    state,
    // Set on a CANCELLATION: the charge it reverses.
    parentId: row.parent_event_id ?? null,
    code: row.service_code || "",
    name: row.service_name || "",
    quantity: num(row.quantity) ?? 1,
    unitPrice: num(row.unit_price),
    gross,
    signedGross: kind === CANCELLATION ? -gross : gross,
    ignored: kind === CHARGE && state === IGNORED,
    pending: kind === CHARGE && state === PENDING,
    chargedAt: row.event_time || row.created_at || null,
    remarks: row.remarks || "",
    // Billing's Charge Capture tab and catalogue group/classification, worked
    // out by the server (label_charges) as for an admission's charges.
    category: row.category_chip || "MISC",
    group: row.group_name || "",
    classification: row.classification_name || "",
  };
}

/**
 * The ledger in the order it happened, with each charge told whether it has
 * been reversed.
 *
 * A charge is reversed iff some cancellation names it as its parent — there is
 * no status field that says so, and there cannot be one, because the reversal
 * is a separate event. An ignored charge is not reversed in this sense: it
 * never processed, so nothing was ever written against it.
 *
 * The service does not return the events in any particular order, so they are
 * sorted here rather than in the component.
 */
export function buildLedger(rows = []) {
  const reversedIds = new Set(
    rows
      .filter((r) => r.kind === CANCELLATION && r.parentId)
      .map((r) => String(r.parentId))
  );

  return [...rows]
    .sort(
      (a, b) =>
        new Date(a.chargedAt || 0).getTime() - new Date(b.chargedAt || 0).getTime()
    )
    .map((r) => ({
      ...r,
      reversed: r.kind === CHARGE && reversedIds.has(String(r.id)),
    }));
}

/**
 * What was charged, what was pulled back, and what still stands.
 *
 * Reversals are shown rather than netted away silently — the ledger is an audit
 * trail, and a charge someone had to reverse is exactly the one a query later
 * will be about.
 *
 * An ignored charge counts as charged and as reversed but never toward what
 * stands: it was typed and pulled back before it reached the ledger, and it has
 * no cancellation event of its own to subtract it.
 */
export function chargeTotals(rows = []) {
  return rows.reduce(
    (acc, row) => {
      const gross = Number(row?.gross) || 0;
      if (row?.kind === CANCELLATION) {
        acc.cancelled += gross;
        acc.net -= gross;
        return acc;
      }
      acc.gross += gross;
      if (row?.ignored) acc.cancelled += gross;
      else acc.net += gross;
      return acc;
    },
    { gross: 0, cancelled: 0, net: 0 }
  );
}

/**
 * Split a capture response into the lines that posted and the lines that did not.
 *
 * Charges are always sent in the bulk form, so each line succeeds or fails on its
 * own and the clerk is told which one to fix. Results are paired with lines by
 * array position, not by the response's 1-based `index`: misattributing an error
 * would tell someone to correct the wrong charge.
 *
 * A response with no `results` array is the single-capture shape. Getting here at
 * all means the envelope said Success, so every line was accepted.
 */
export function summarizeCapture(body, lines = []) {
  const results = Array.isArray(body?.results) ? body.results : null;
  if (!results) return { posted: [...lines], failed: [] };

  const posted = [];
  const failed = [];
  results.forEach((result, i) => {
    if (result?.ok === false) {
      failed.push({
        line: lines[i],
        error: result?.error || "The charge was not accepted",
      });
    } else {
      posted.push(lines[i]);
    }
  });
  return { posted, failed };
}

/** Rupees, Indian digit grouping. An absent amount reads as a dash, not ₹0.00. */
export function formatINR(value) {
  const n = num(value);
  if (n === null) return "—";
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * How many things on a visit have not reached billing: open charges (pending or
 * refused) and doses given with no stocked item chosen. Takes `getChargeCheck()`'s
 * shape; zero when nothing was loaded.
 */
export function chargeCheckCount(check) {
  return (check?.open?.length || 0) + (check?.dosesToPrice?.length || 0);
}
